/**
 * useRealtime — a tiny WebSocket hook for Diplomexa live-update topics.
 *
 * Usage:
 *   useRealtime("poll:abc123", (msg) => setResults(msg.results));
 *   useRealtime(["crisis:evt-1", "notifications:u-9"], handler);
 *
 * Auto-reconnects with exponential back-off (1s, 2s, 4s, 8s … capped at 15s)
 * and re-joins all topics after reconnect.  Sends a 25s heartbeat ping.
 */
import { useEffect, useRef } from "react";
import { getToken } from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function buildWsUrl(token, topics) {
  const base = (BACKEND_URL || "").replace(/^http/, "ws");
  const qs = new URLSearchParams();
  if (token) qs.set("token", token);
  if (topics?.length) qs.set("subscribe", topics.join(","));
  return `${base}/api/ws?${qs.toString()}`;
}

export function useRealtime(topics, onMessage, options = {}) {
  // Normalise input
  const topicArr = Array.isArray(topics) ? topics.filter(Boolean) : topics ? [topics] : [];
  const stableTopics = topicArr.join("|");
  const handlerRef = useRef(onMessage);
  handlerRef.current = onMessage;

  useEffect(() => {
    if (!stableTopics) return undefined;
    const arr = stableTopics.split("|");
    const token = getToken();
    let ws;
    let closed = false;
    let attempt = 0;
    let pingInterval;

    const open = () => {
      try {
        ws = new WebSocket(buildWsUrl(token, arr));
      } catch (e) {
        return;
      }
      ws.onopen = () => {
        attempt = 0;
        pingInterval = setInterval(() => {
          try {
            ws?.send(JSON.stringify({ action: "ping" }));
          } catch { /* socket already closed; reconnect handler will recover */ }
        }, 25000);
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.action === "pong" || msg.action === "ready" || msg.action === "joined") return;
          handlerRef.current?.(msg);
        } catch { /* non-JSON frame — silently drop, server only sends JSON */ }
      };
      ws.onclose = () => {
        if (pingInterval) clearInterval(pingInterval);
        if (closed) return;
        attempt += 1;
        const delay = Math.min(15000, 1000 * 2 ** attempt);
        setTimeout(open, delay);
      };
      ws.onerror = () => {
        try {
          ws?.close();
        } catch { /* already closing — onclose handler will reconnect */ }
      };
    };

    open();
    return () => {
      closed = true;
      if (pingInterval) clearInterval(pingInterval);
      try {
        ws?.close();
      } catch { /* unmount cleanup — socket state irrelevant */ }
    };
  }, [stableTopics]);
}
